import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrandedCard, BrandedHeader, BrandedButton, BrandedSelect, BrandedInput, BrandedAlert } from './branding/BrandedComponents';
import { useAuth } from '../contexts/AuthContext';
import platformApi from '../services/platformApi';

function celebrateWithConfetti() {
  const doc = document;
  if (!doc.getElementById('confetti-style')) {
    const style = doc.createElement('style');
    style.id = 'confetti-style';
    style.textContent = `
      @keyframes confettiFall {
        0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
        100% { transform: translateY(110vh) rotate(720deg); opacity: 0.9; }
      }
      .confetti-piece {
        position: fixed;
        top: -10vh;
        width: 8px;
        height: 14px;
        opacity: 0.9;
        border-radius: 2px;
        animation: confettiFall linear forwards;
        z-index: 9999;
      }
    `;
    doc.head.appendChild(style);
  }

  const colors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];
  const count = 120;
  for (let i = 0; i < count; i++) {
    const piece = doc.createElement('div');
    piece.className = 'confetti-piece';
    const left = Math.random() * 100;
    const delay = Math.random() * 0.5;
    const duration = 2 + Math.random() * 1.5;
    const color = colors[i % colors.length];
    piece.style.left = `${left}vw`;
    piece.style.backgroundColor = color;
    piece.style.animationDuration = `${duration}s`;
    piece.style.animationDelay = `${delay}s`;
    doc.body.appendChild(piece);
    setTimeout(() => piece.remove(), (delay + duration) * 1000);
  }
}

const crmOptions = [
  { value: '', label: 'Select your CRM' },
  { value: 'hubspot', label: 'HubSpot' },
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'pipedrive', label: 'Pipedrive' },
  { value: 'zoho', label: 'Zoho CRM' },
  { value: 'none', label: 'None' },
  { value: 'other', label: 'Other' },
];

const useCaseOptions = [
  { value: 'support', label: 'Customer Support Video Calls' },
  { value: 'sales', label: 'Sales Demos / Consultations' },
  { value: 'healthcare', label: 'Telehealth Appointments' },
  { value: 'legal', label: 'Legal Consultations' },
  { value: 'home_improvement', label: 'Home Improvement Estimates' },
  { value: 'interview', label: 'Interview / Hiring' },
];

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { getCurrentOrganizationId } = useAuth();

  const [agencyName, setAgencyName] = useState('');
  const [crm, setCrm] = useState('');
  const [customCrm, setCustomCrm] = useState('');
  const [useCases, setUseCases] = useState<string[]>([]);
  const [otherNotes, setOtherNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleToggleUseCase = (value: string) => {
    setUseCases((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const orgId = params.orgId || getCurrentOrganizationId();
      // Resolve CRM to save (if Other, require custom value)
      const crmToSave = crm === 'other' ? customCrm.trim() : crm;
      if (crm === 'other' && !crmToSave) {
        setError('Please specify your CRM');
        setSaving(false);
        return;
      }

      await platformApi.saveOnboarding(orgId, {
        agencyName,
        crm: crmToSave,
        useCases,
        otherNotes,
        completedAt: new Date().toISOString(),
      });
      celebrateWithConfetti();
      navigate(`/${orgId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save onboarding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-brand-background">
      <BrandedCard className="w-full max-w-2xl" padding="large">
        <BrandedHeader size="large" customTagline="Let’s tailor your setup" />
        {error && (
          <BrandedAlert className="mb-6" variant="error">{error}</BrandedAlert>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <BrandedInput
            label="Agency / Company Name"
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            placeholder="Acme Agency"
            required
          />

          <BrandedSelect
            label="CRM"
            value={crm}
            onChange={(e) => setCrm(e.target.value)}
            options={crmOptions}
          />

        {crm === 'other' && (
          <BrandedInput
            label="Your CRM"
            value={customCrm}
            onChange={(e) => setCustomCrm(e.target.value)}
            placeholder="Enter your CRM vendor (e.g., Microsoft Dynamics)"
            required
          />
        )}

          <div>
            <label className="block text-sm font-medium mb-2 text-brand-primary">Primary Use Cases</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {useCaseOptions.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-brand-secondary">
                  <input
                    type="checkbox"
                    checked={useCases.includes(opt.value)}
                    onChange={() => handleToggleUseCase(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <BrandedInput
            label="What else should we know? (optional)"
            value={otherNotes}
            onChange={(e) => setOtherNotes(e.target.value)}
            placeholder="Integrations, goals, or timelines"
          />

          <div className="flex justify-end">
            <BrandedButton type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Finish Onboarding'}
            </BrandedButton>
          </div>
        </form>
      </BrandedCard>
    </div>
  );
};

export default OnboardingPage;


